import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { Alert, Badge, Button, InlineLoader } from '../../components/ui'
import { ApiError, ContractError, NetworkError } from '../../api/client'
import { addChat, fetchChatHistory, fetchDialogs } from '../../api/chats'
import type { AddedChat, BrowsedChat, HistoryLine } from '../../api/chats'
import { keys } from '../../api/keys'
import { t } from '../../i18n'
import type { MessageKey } from '../../i18n'
import { useSession } from '../../auth/session'
import { shortTime } from '../../format'

/**
 * Adding a conversation that started before the system did.
 *
 * The shape of this screen is the shape of the decision behind it (docs/adr/0012
 * — it is worth reading before changing anything here).
 *
 * The list is the owner's private life. It is fetched when this opens, held
 * nowhere, and dropped when it closes: no query cache, no persistence, nothing
 * that outlives the modal. A chat the owner put on the ignorelist is shown as
 * unavailable rather than hidden, because "why is my mother not in this list" is
 * a question the screen should answer itself.
 *
 * Clicking a conversation reads it. That is a request to Telegram over somebody
 * else's account, so it happens when a person asks and not before — no
 * prefetching the whole address book to make the list feel fast.
 */
export function AddChat() {
  const { can } = useSession()
  const [open, setOpen] = useState(false)

  if (!can('channels.browse')) return null

  return (
    <>
      <Button variant="secondary" onClick={() => setOpen(true)}>
        {t('addChat.open')}
      </Button>
      {open ? <ChatChooser onClose={() => setOpen(false)} /> : null}
    </>
  )
}

function messageFor(error: unknown): string {
  if (error instanceof ApiError) return error.message
  if (error instanceof NetworkError) return t('errors.network')
  if (error instanceof ContractError) return t('errors.badResponse')
  return t('addChat.failed')
}

function ChatChooser({ onClose }: { readonly onClose: () => void }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const [chosen, setChosen] = useState<BrowsedChat | null>(null)
  const [added, setAdded] = useState<AddedChat | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  // How far back the imports have reached, kept apart from the result of the
  // last one. Telegram is read backwards from a message id, so this is the only
  // thing that makes "more history" mean anything: reading it out of `added`
  // meant that anything clearing `added` sent the next call back to id 0, which
  // re-read the same last batch, stored nothing, and still reported that older
  // history remained. A person could press for ever without moving.
  const [oldest, setOldest] = useState(0)
  // Everything this window has brought in for the chosen chat, so that pressing
  // twice does not report the second batch as if it were the whole import.
  const [broughtIn, setBroughtIn] = useState(0)
  // The oldest date reached. Kept because a press that finds nothing older
  // reports no date of its own, and the sentence on the screen is about how far
  // back the conversation now goes, not about the last press.
  const [reachedFrom, setReachedFrom] = useState('')

  const dialogs = useQuery({
    queryKey: ['channel-dialogs'],
    queryFn: ({ signal }) => fetchDialogs(signal),
    // Never reused. The whole point is that this list exists while somebody is
    // looking at it and not afterwards.
    gcTime: 0,
    staleTime: 0,
    retry: false,
  })

  const history = useQuery({
    queryKey: ['channel-history', chosen?.peerId ?? 0],
    queryFn: ({ signal }) =>
      chosen === null ? Promise.resolve([]) : fetchChatHistory(chosen, signal),
    enabled: chosen !== null,
    gcTime: 0,
    staleTime: 0,
    retry: false,
  })

  const add = useMutation({
    mutationFn: ({ chat, beforeId }: { chat: BrowsedChat; beforeId: number }) =>
      addChat(chat, beforeId),
    onSuccess: (result) => {
      setAdded(result)
      setFailure(null)
      // Only ever backwards. A batch that stored nothing new leaves the cursor
      // where it was rather than resetting it to the newest message.
      if (result.oldestId > 0 && (oldest === 0 || result.oldestId < oldest)) {
        setOldest(result.oldestId)
      }
      setBroughtIn((sofar) => sofar + result.imported)
      if (result.from !== '') setReachedFrom(result.from)
      // The conversation is now in the inbox, with work scheduled on it.
      void queryClient.invalidateQueries({ queryKey: keys.anyThreadList() })
    },
    onError: (error) => setFailure(messageFor(error)),
  })

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label={t('addChat.title')}>
      <div className="modal__panel modal__panel--wide">
        <header className="modal__header">
          <h2 className="modal__title">{t('addChat.title')}</h2>
          <Button variant="ghost" onClick={onClose}>
            {t('common.close')}
          </Button>
        </header>

        <p className="modal__hint">{t('addChat.hint')}</p>

        {failure === null ? null : <Alert>{failure}</Alert>}

        <div className="chooser">
          <div className="chooser__list">
            {dialogs.isLoading ? <InlineLoader /> : null}
            {dialogs.isError ? <Alert>{messageFor(dialogs.error)}</Alert> : null}
            {dialogs.data?.length === 0 ? (
              <p className="chooser__empty">{t('addChat.noChats')}</p>
            ) : null}

            {dialogs.data?.map((chat) => (
              <ChatRow
                key={chat.peerId}
                chat={chat}
                selected={chosen?.peerId === chat.peerId}
                onChoose={() => {
                  // Clicking the row already chosen must not throw away how far
                  // back its import has reached.
                  if (chosen?.peerId === chat.peerId) return
                  setChosen(chat)
                  setAdded(null)
                  setOldest(0)
                  setBroughtIn(0)
                  setReachedFrom('')
                  setFailure(null)
                }}
              />
            ))}
          </div>

          <div className="chooser__detail">
            {chosen === null ? (
              <p className="chooser__empty">{t('addChat.pickOne')}</p>
            ) : (
              <ChatPreview
                chat={chosen}
                lines={history.data ?? []}
                loading={history.isLoading}
                error={history.isError ? messageFor(history.error) : null}
                added={added}
                broughtIn={broughtIn}
                reachedFrom={reachedFrom}
                busy={add.isPending}
                onAdd={() =>
                  // Continue behind what is already here, so pressing "more"
                  // reaches further back rather than re-reading the same batch.
                  add.mutate({ chat: chosen, beforeId: oldest })
                }
                onOpen={() => {
                  if (added === null) return
                  // Close before navigating. The dialog sits in InboxLayout,
                  // which is the parent route of both /inbox and
                  // /inbox/:threadId, so navigating does not unmount it: the
                  // conversation opened underneath and the window stayed on top
                  // of it.
                  onClose()
                  navigate(`/inbox/${added.threadId}`)
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * One row of the chooser.
 *
 * Exported so the checks can render it on its own: what it shows about a chat
 * that cannot be added is a rule (docs/adr/0012), not styling, and a rule with
 * no test is a rule until somebody tidies it away.
 */
export function ChatRow({
  chat,
  selected,
  onChoose,
}: {
  readonly chat: BrowsedChat
  readonly selected: boolean
  readonly onChoose: () => void
}) {
  const classes = ['chooser__row']
  if (selected) classes.push('chooser__row--selected')
  if (!chat.admissible) classes.push('chooser__row--blocked')

  return (
    <button type="button" className={classes.join(' ')} onClick={onChoose}>
      <span className="chooser__row-head">
        <span className="chooser__row-title">{chat.title}</span>
        {chat.known ? <Badge tone="muted">{t('addChat.alreadyHere')}</Badge> : null}
        {chat.admissible ? null : <Badge tone="danger">{t(refusalKey(chat.refused))}</Badge>}
      </span>
      {chat.preview === '' ? null : (
        <span className="chooser__row-preview">
          {chat.out ? `${t('addChat.you')}: ` : ''}
          {chat.preview}
        </span>
      )}
      {chat.lastMessageAt === undefined ? null : (
        <span className="chooser__row-date">{shortTime(chat.lastMessageAt)}</span>
      )}
    </button>
  )
}

/**
 * The chosen chat and what to do with it.
 *
 * Exported for the same reason ChatRow is: which buttons survive an import is a
 * rule -- the note telling somebody to press «Загрузить ещё историю» has to name
 * a button that is on the screen -- and a rule with no check is a rule until
 * somebody tidies it away.
 */
export function ChatPreview({
  chat,
  lines,
  loading,
  error,
  added,
  broughtIn,
  reachedFrom,
  busy,
  onAdd,
  onOpen,
}: {
  readonly chat: BrowsedChat
  readonly lines: readonly HistoryLine[]
  readonly loading: boolean
  readonly error: string | null
  readonly added: AddedChat | null
  /** Everything this window has imported for the chat, across presses. */
  readonly broughtIn: number
  /** The oldest date reached, across presses. */
  readonly reachedFrom: string
  readonly busy: boolean
  readonly onAdd: () => void
  readonly onOpen: () => void
}) {
  return (
    <>
      <header className="chooser__detail-head">
        <h3 className="chooser__detail-title">{chat.title}</h3>
        {chat.username === '' ? null : (
          <span className="chooser__detail-username">@{chat.username}</span>
        )}
      </header>

      {chat.admissible ? null : (
        <Alert tone="warning">{t('addChat.blockedExplained')}</Alert>
      )}

      {loading ? <InlineLoader /> : null}
      {error === null ? null : <Alert>{error}</Alert>}

      {/*
        Newest first, as Telegram answers. Not reversed into reading order: this
        is a peek to answer "is this a client", and the last thing said is the
        part that answers it.
      */}
      <ol className="chooser__messages">
        {lines.map((line) => (
          <li
            className={line.out ? 'chooser__message chooser__message--out' : 'chooser__message'}
            key={line.messageId}
          >
            <span className="chooser__message-who">
              {line.out ? t('addChat.you') : chat.title}
            </span>
            <span className="chooser__message-text">
              {line.text === '' && line.media > 0 ? t('addChat.attachmentOnly') : line.text}
            </span>
            <span className="chooser__message-at">{shortTime(line.at)}</span>
          </li>
        ))}
      </ol>

      <footer className="chooser__actions">
        {added === null ? (
          <Button busy={busy} disabled={!chat.admissible} onClick={onAdd}>
            {chat.known ? t('addChat.addMore') : t('addChat.add')}
          </Button>
        ) : (
          <>
            <p className="chooser__result">
              {t('addChat.added', {
                imported: broughtIn,
                from: reachedFrom === '' ? '—' : shortTime(reachedFrom),
              })}
            </p>
            {added.more ? <p className="chooser__note">{t('addChat.moreLeft')}</p> : null}
            {/*
              The button the note points at. It used to disappear the moment the
              first import succeeded, so the sentence "press «load more
              history»" named something that was no longer on the screen.
            */}
            {added.more ? (
              <Button variant="secondary" busy={busy} onClick={onAdd}>
                {t('addChat.addMore')}
              </Button>
            ) : null}
            <Button onClick={onOpen}>{t('addChat.openThread')}</Button>
          </>
        )}
      </footer>
    </>
  )
}

/**
 * The scope filter's reason, in words somebody would use.
 *
 * An unknown reason falls back to a plain "cannot be added": a server newer than
 * this bundle should not put a snake_case token on the screen.
 */
function refusalKey(reason: string): MessageKey {
  switch (reason) {
    case 'ignorelisted':
      return 'addChat.refusedIgnorelisted'
    case 'bot':
      return 'addChat.refusedBot'
    case 'not_a_private_dialog':
      return 'addChat.refusedNotPrivate'
    case 'service_account':
      return 'addChat.refusedService'
    case 'self':
      return 'addChat.refusedSelf'
    default:
      return 'addChat.refusedOther'
  }
}
